// src/components/EchSim.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react'; // Added useCallback
import { useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom'; // Added for sidebar navigation
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import {
    // Sidebar Needs (Simplified):
    LayoutDashboard, // Back to Dashboard
    Mail, // User Email Icon
    // Top Bar Needs:
    Search, Bell, ChevronDown, Menu, Globe, LifeBuoy,
    // Simulation Page Needs:
    FlaskConical, // Page Title Icon
    Loader2, X, ShieldAlert, Send, ClipboardCopy, Check,
    Scale, BrainCircuit, Play, Star, BarChart, TrendingUp, Users2, Zap, Target, FileText, Lightbulb, AlertTriangle, ListChecks, Activity as ActivityIcon, // Renamed Activity to avoid conflict
    // Retained but likely unused:
    Settings, Users, Bot, Database, Link, BookOpen, Cpu, Wrench, HelpCircle, Server, DollarSign
} from 'lucide-react';

// --- API Configuration (PRESERVED) ---
const API_BASE_URL = 'https://web-production-5ee8.up.railway.app'; // For running simulation
const AGENT_FETCH_URL = 'https://web-production-5ee8.up.railway.app/echidna/myagents'; // For fetching agents

// --- Styled Clickable Components (Unchanged) ---
const ClickableLink = ({ children, className, href = '#', target, rel, onClick, disabled, title, ...props }) => (
    <a href={!disabled ? href : undefined} target={target} rel={target === '_blank' ? 'noopener noreferrer' : rel} onClick={!disabled ? onClick : (e) => e.preventDefault()} role="button" tabIndex={disabled ? -1 : 0} className={`inline-flex items-center justify-center transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded-md ${ disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer' } ${className}`} aria-disabled={disabled} title={title || (typeof children === 'string' ? children : '')} {...props}>{children}</a>
);
const ClickableDiv = ({ children, className, onClick, disabled, title, ...props }) => (
    <div role="button" tabIndex={disabled ? -1 : 0} onClick={!disabled ? onClick : undefined} onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) onClick?.(e); }} className={`inline-flex items-center justify-center transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded-md ${ disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer' } ${className}`} aria-disabled={disabled} title={title || (typeof children === 'string' ? children : '')} {...props}>{children}</div>
);

// --- Sidebar Nav Item Component (Reused Structure) ---
const SidebarNavItem = ({ icon: Icon, text, href, active, onClick }) => ( // Active prop kept for potential future reuse, but not used here
     <a href={href} onClick={onClick} className={`flex items-center pl-3 pr-2 py-1.5 rounded-md text-xs font-medium transition-colors duration-150 group relative text-gray-400 hover:text-white hover:bg-gray-800/50`}>
         {/* Active indicator removed */}
         <Icon className={`w-4 h-4 mr-2.5 flex-shrink-0 text-gray-500 group-hover:text-gray-300`} strokeWidth={1.5} />
         <span className="truncate">{text}</span>
     </a>
);

// --- Simplified Sidebar Component ---
const Sidebar = () => {
    const navigate = useNavigate(); // Use navigate for routing
    const { user, isLoaded } = useUser(); // Get user object and loading state

    const handleBackToDashboardClick = (e) => {
        e.preventDefault(); // Prevent default anchor tag behavior
        console.log("Navigating back to /echidna/efoundry/dashboard");
        navigate('/echidna/efoundry/dashboard'); // Navigate using React Router
    };

    // Use primary email address, provide loading/fallback state
    const userEmail = isLoaded && user ? user.primaryEmailAddress?.emailAddress : 'Loading...';

    return (
        <div className="w-52 md:w-56 h-screen bg-black border-r border-gray-800/50 flex flex-col flex-shrink-0 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700/50 scrollbar-track-black">
            {/* Header */}
            <div className="h-14 flex items-center px-3 flex-shrink-0 border-b border-gray-800/50">
                <div className="flex items-center">
                    <svg className="w-6 h-6 mr-1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#8B5CF6" stroke="#8B5CF6" strokeWidth="1.5"/><path d="M2 17L12 22L22 17" stroke="#8B5CF6" strokeWidth="1.5"/><path d="M2 12L12 17L22 12" stroke="#8B5CF6" strokeWidth="1.5"/></svg>
                    <span className="text-base font-bold text-white">Echidna Foundry</span>
                </div>
            </div>

            {/* Simplified Navigation Area */}
            <div className="flex-grow py-2">
                <nav className="space-y-0.5 px-2 mt-3"> {/* Added mt-3 for spacing */}
                    <SidebarNavItem
                        key="back-dashboard"
                        icon={LayoutDashboard}
                        text="Back to Dashboard"
                        href="/echidna/efoundry/dashboard" // href for context/right-click
                        active={false} // This item is never 'active' within this view
                        onClick={handleBackToDashboardClick} // Use navigate handler
                    />
                </nav>
            </div>

            {/* Updated User Info Section */}
            <div className="p-2 border-t border-gray-800/50 mt-auto">
                 <ClickableLink
                    href="#profile" // Placeholder href
                    title={userEmail} // Tooltip shows the email
                    className="flex items-center p-2 rounded-md text-xs text-gray-400 hover:bg-gray-800/50 hover:text-white group w-full" // Added w-full
                 >
                    <Mail className="w-4 h-4 mr-2 text-gray-500 group-hover:text-gray-300 flex-shrink-0" strokeWidth={1.5} />
                    <span className="truncate font-medium">{userEmail}</span>
                 </ClickableLink>
             </div>
        </div>
    );
};

// --- Top Bar Component (Updated User Info Fallback) ---
const TopBar = ({ pageTitle: pageTitleProp }) => {
    const { user } = useUser();
    // Use email as primary fallback for display name and initials
    const userInitial = user?.firstName?.charAt(0) || user?.lastName?.charAt(0) || user?.primaryEmailAddress?.emailAddress?.[0] || '?';
    const userDisplayName = user?.firstName ? `${user.firstName}${user.lastName ? ' ' + user.lastName.charAt(0) + '.' : ''}` : user?.primaryEmailAddress?.emailAddress || 'Account';
    const pageTitle = pageTitleProp || 'Simulations'; // Default title if none provided

    return ( <div className="h-14 bg-gray-950 border-b border-gray-800/50 flex items-center justify-between px-4 flex-shrink-0 sticky top-0 z-20"> <div className="flex items-center space-x-3"><ClickableDiv className="p-1.5 -m-1.5 rounded hover:bg-gray-800/50 text-gray-300 hover:text-white" title="Services Menu"><Menu className="w-5 h-5" strokeWidth={1.5}/></ClickableDiv><span className="text-sm font-medium text-white">{pageTitle}</span></div> <div className="flex items-center space-x-3 md:space-x-4"><div className="relative hidden md:block"><Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" strokeWidth={1.5}/><input type="search" placeholder={`Search ${pageTitle}...`} className="bg-gray-800/60 border border-gray-700/50 rounded-md pl-8 pr-2 py-1 w-48 lg:w-64 text-xs text-gray-200 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 focus:outline-none focus:bg-gray-800" /></div><ClickableDiv className="flex items-center text-xs text-gray-400 hover:text-white p-1.5 -m-1.5 rounded hover:bg-gray-800/50" title="Region"><Globe className="w-4 h-4 mr-1" strokeWidth={1.5}/><span>us-east-1</span></ClickableDiv><ClickableLink href="#help" title="Support" className="text-gray-400 hover:text-white p-1.5 -m-1.5 rounded hover:bg-gray-800/50"><LifeBuoy className="w-5 h-5" strokeWidth={1.5}/></ClickableLink><ClickableDiv title="Notifications" className="text-gray-400 hover:text-white relative p-1.5 -m-1.5 rounded hover:bg-gray-800/50"><Bell className="w-5 h-5" strokeWidth={1.5}/></ClickableDiv>
    <ClickableDiv title="Account" className="flex items-center text-xs text-gray-300 hover:text-white p-1.5 -m-1.5 rounded hover:bg-gray-800/50">
        <span className="hidden md:inline mr-1 truncate max-w-[100px]" title={userDisplayName}>{userDisplayName}</span>
        <span className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">{userInitial.toUpperCase()}</span>
        <ChevronDown className="w-4 h-4 ml-1 text-gray-500" strokeWidth={1.5}/>
    </ClickableDiv>
    </div> </div> );
};

// --- Helper Function: Parse Reasoning --- (Unchanged)
function parseReasoningResponse(rawText) { if (typeof rawText !== 'string') { console.warn("parseReasoningResponse non-string:", rawText); return { reasoning: null, response: String(rawText) }; } const reasonStartTag = '<reason>'; const reasonEndTag = '</reason>'; const separator = '\n\n'; let reasoning = null; let response = rawText; const startIdx = rawText.indexOf(reasonStartTag); if (startIdx !== -1) { const endIdx = rawText.indexOf(reasonEndTag, startIdx + reasonStartTag.length); if (endIdx !== -1) { reasoning = rawText.substring(startIdx + reasonStartTag.length, endIdx).trim(); const followingText = rawText.substring(endIdx + reasonEndTag.length); if (followingText.trimStart().startsWith(separator)) { response = followingText.substring(followingText.indexOf(separator) + separator.length).trimStart(); } else if (followingText.trim().length > 0) { response = followingText.trimStart(); } else { response = null; } } else { reasoning = rawText.substring(startIdx + reasonStartTag.length).trimStart(); response = null; } } if (typeof response === 'string') { response = response.trim(); } if (reasoning === '') reasoning = null; if (response === '') response = null; return { reasoning, response }; }

// --- Markdown Components Configuration --- (Unchanged)
const markdownComponents = { code({ node, inline, className, children, ...props }) { const match = /language-(\w+)/.exec(className || ''); return !inline && match ? ( <SyntaxHighlighter style={vscDarkPlus} PreTag="div" language={match[1]} {...props} className="scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">{String(children).replace(/\n$/, '')}</SyntaxHighlighter> ) : ( <code className="bg-gray-700/50 px-1 py-0.5 rounded text-xs font-mono" {...props}>{children}</code> ); }, p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />, ul: ({ node, ...props }) => <ul className="list-disc list-outside mb-2 pl-4" {...props} />, ol: ({ node, ...props }) => <ol className="list-decimal list-outside mb-2 pl-4" {...props} />, li: ({ node, ...props }) => <li className="mb-1" {...props} />, a: ({ node, ...props }) => <a className="text-purple-400 hover:text-purple-300 hover:underline" target="_blank" rel="noopener noreferrer" {...props} />, pre: ({ node, ...props }) => <pre className="bg-gray-900 p-0 rounded-md overflow-hidden" {...props} />, };

// --- Helper Components for Simulation Results Display --- (Unchanged)

// Card for displaying a single metric or score
const MetricCard = ({ title, value, unit = '', icon: Icon, description, color = 'text-purple-400' }) => (
    <div className="bg-gray-900 p-3 rounded-md border border-gray-700/50">
        <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-gray-400 flex items-center">
                {Icon && <Icon className={`w-3.5 h-3.5 mr-1.5 ${color}`} strokeWidth={2} />}
                {title}
            </span>
            <span className={`text-sm font-semibold ${color}`}>
                {value !== null && value !== undefined ? `${value}${unit}` : 'N/A'}
            </span>
        </div>
        {description && <p className="text-[11px] text-gray-500">{description}</p>}
    </div>
);

// Section for displaying analysis lists (strengths, weaknesses, etc.)
const AnalysisListSection = ({ title, items, icon: Icon, itemColor = 'text-gray-300' }) => (
    items && items.length > 0 ? (
        <div className="mb-4">
            <h4 className="text-xs font-semibold text-gray-400 mb-1.5 flex items-center">
                {Icon && <Icon className="w-3.5 h-3.5 mr-1.5 text-purple-400" strokeWidth={2} />}
                {title}
            </h4>
            <ul className="list-disc list-outside pl-5 space-y-1">
                {items.map((item, index) => (
                    <li key={index} className={`text-xs ${itemColor}`}>{item}</li>
                ))}
            </ul>
        </div>
    ) : null
);

// Component to format numbers nicely
const formatNumber = (num, options = {}) => {
    const { decimals = 2, type = 'decimal' } = options;
    if (num === null || num === undefined || isNaN(num)) {
        return 'N/A';
    }
    if (type === 'percent') {
        return `${(num * 100).toFixed(decimals)}%`;
    }
    if (type === 'score') { // Assuming score out of 1
         return num.toFixed(decimals);
    }
    if (type === 'ms') {
        return `${Math.round(num)} ms`;
    }
    if (type === 'integer') {
        return Math.round(num).toString();
    }
    return num.toFixed(decimals);
};

// --- Simulation Result Display Component (Unchanged) ---
const SimulationResultDisplay = ({ simulationData }) => {
    const { results: simResults, status, error } = simulationData || { status: 'idle', results: null, error: null };
    const [copiedId, setCopiedId] = useState(false);

    const handleCopySimId = () => {
        if (!simResults?.simulation_id) return;
        navigator.clipboard.writeText(simResults.simulation_id).then(() => {
            setCopiedId(true);
            setTimeout(() => setCopiedId(false), 1500);
        }).catch(err => console.error('Failed to copy sim ID: ', err));
    };

    // Early return for non-success states
    if (status === 'loading') {
        return ( <div className="bg-gray-950 rounded-lg border border-gray-800/50 p-6 mt-6 flex items-center justify-center min-h-[300px] text-gray-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /><span>Running Simulation...</span></div> );
    }
    if (status === 'error') {
        return ( <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-6 mt-6 text-center text-red-400 min-h-[300px] flex flex-col items-center justify-center"><ShieldAlert className="w-8 h-8 mx-auto mb-2" />Error Running Simulation:<p className="text-sm mt-1">{error || 'Unknown error'}</p></div> );
    }
    if (status === 'idle' || !simResults) {
        return ( <div className="bg-gray-950 rounded-lg border border-gray-800/50 p-6 mt-6 flex items-center justify-center min-h-[300px] text-gray-600 italic">Configure and run a simulation to see results here.</div> );
    }

    // --- Render Success State ---
    const metrics = simResults.metrics || {};
    const analysis = simResults.analysis || {};
    const benchmark = simResults.benchmark || {};

    // Helper for safe metric access
    const getMetric = (path, defaultValue = 'N/A', formatOptions = {}) => {
        const value = path.split('.').reduce((o, k) => (o || {})[k], metrics);
        return formatNumber(value, formatOptions) ?? defaultValue;
    }

    return (
        <div className="bg-gray-950 rounded-lg border border-gray-800/50 flex flex-col shadow-sm mt-6 text-sm">
            {/* Header */}
            <div className="flex justify-between items-start p-4 border-b border-gray-800/50 flex-shrink-0">
                <div>
                    <h3 className="text-base font-semibold text-gray-100 mb-1">Simulation Results</h3>
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                        <span>ID: {simResults.simulation_id}</span>
                        <ClickableDiv onClick={handleCopySimId} title={copiedId ? "Copied!" : "Copy Simulation ID"} className="p-0.5 -m-0.5 text-gray-600 hover:text-purple-400 rounded">
                            {copiedId ? <Check className="w-3 h-3 text-green-500" strokeWidth={2} /> : <ClipboardCopy className="w-3 h-3" strokeWidth={1.5} />}
                        </ClickableDiv>
                    </div>
                </div>
                <div className="text-right flex-shrink-0 ml-4">
                     <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${simResults.resolved ? 'bg-green-700/30 text-green-300' : 'bg-yellow-700/30 text-yellow-300'}`}>
                         {simResults.resolved ? 'Resolved' : 'Not Resolved'}
                     </span>
                     <p className="text-xs text-gray-500 mt-1">Duration: {formatNumber(simResults.duration_ms, { type: 'ms' })}</p>
                </div>
            </div>

            {/* Body Content */}
            <div className="p-4 flex-grow overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700/50 scrollbar-track-gray-950">
                {/* Overall Performance Card */}
                <div className="mb-6">
                   <MetricCard
                       title="Overall Performance Score"
                       value={getMetric('overall_performance', 'N/A', { decimals: 3 })}
                       icon={Star}
                       description={`Agent Rating: ${simResults.benchmark?.industry_comparison?.overallPerformance || 'N/A'}`}
                       color={parseFloat(metrics.overall_performance) > 0.75 ? 'text-green-400' : parseFloat(metrics.overall_performance) > 0.5 ? 'text-yellow-400' : 'text-red-400'}
                    />
                </div>

                {/* --- Metrics Section --- */}
                <details className="mb-4" open>
                    <summary className="text-sm font-semibold text-gray-300 hover:text-white cursor-pointer select-none mb-3 flex items-center gap-1.5">
                       <BarChart className="w-4 h-4 text-purple-400" /> Key Metrics
                    </summary>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pl-1">
                        <MetricCard title="Resolution Score" value={getMetric('resolution.score', 'N/A', { decimals: 3 })} icon={Target} description={`Percentile: ${getMetric('resolution.percentile', 'N/A', { type: 'percent', decimals: 1 })}`} />
                        <MetricCard title="Response Quality" value={getMetric('response_quality.overall_score', 'N/A', { decimals: 3 })} icon={Check} description={`Accuracy: ${getMetric('response_quality.accuracy', 'N/A', { decimals: 2 })}`} />
                        <MetricCard title="Efficiency Score" value={getMetric('efficiency.efficiency_score', 'N/A', { decimals: 3 })} icon={Zap} description={`Avg Resp Time: ${getMetric('efficiency.avg_response_time_ms', 'N/A', { type: 'ms' })}`} />
                        <MetricCard title="Sentiment Change" value={getMetric('sentiment.change', 'N/A', { decimals: 3 })} icon={TrendingUp} description={`Final Sentiment: ${getMetric('sentiment.final', 'N/A', { decimals: 2 })}`} />
                        <MetricCard title="Adaptability" value={getMetric('adaptability', 'N/A', { decimals: 3 })} icon={Users2} description="Agent's ability to adjust" />
                         <MetricCard title="Context Retention" value={getMetric('response_quality.context_retention', 'N/A', { decimals: 3 })} icon={BrainCircuit} description="Ability to remember past messages" />
                    </div>
                </details>

                 {/* --- Conversation Transcript --- */}
                 <details className="mb-4">
                    <summary className="text-sm font-semibold text-gray-300 hover:text-white cursor-pointer select-none mb-3 flex items-center gap-1.5">
                       <FileText className="w-4 h-4 text-purple-400" /> Conversation Transcript ({simResults.conversation?.length || 0} messages)
                    </summary>
                    <div className="space-y-3 max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800 bg-gray-900 border border-gray-700/50 rounded-md p-3">
                        {simResults.conversation?.map((msg, index) => (
                            <div key={index} className={`flex ${msg.sender === 'user' ? 'justify-start' : 'justify-end'}`}>
                                <div className={`max-w-[85%] px-3 py-1.5 rounded-lg text-xs leading-relaxed ${
                                    msg.sender === 'user'
                                        ? 'bg-gray-700/80 text-gray-200 rounded-br-none'
                                        : 'bg-purple-800/60 text-purple-100 rounded-bl-none'
                                }`}>
                                    <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
                                        {msg.text || ''}
                                    </ReactMarkdown>
                                </div>
                            </div>
                        ))}
                        {(!simResults.conversation || simResults.conversation.length === 0) && <p className="text-xs text-gray-500 italic">No conversation recorded.</p>}
                    </div>
                </details>

                {/* --- Analysis Section --- */}
                <details className="mb-4">
                    <summary className="text-sm font-semibold text-gray-300 hover:text-white cursor-pointer select-none mb-3 flex items-center gap-1.5">
                       <Lightbulb className="w-4 h-4 text-purple-400" /> Analysis & Insights
                    </summary>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 pl-1 text-xs">
                        <AnalysisListSection title="Strengths" items={analysis.strengths} icon={Check} itemColor="text-green-300" />
                        <AnalysisListSection title="Weaknesses" items={analysis.weaknesses} icon={AlertTriangle} itemColor="text-yellow-300" />
                        <AnalysisListSection title="Key Insights" items={analysis.insights} icon={Lightbulb} />
                        {analysis.improvementAreas && (
                             <div>
                                 <h4 className="text-xs font-semibold text-gray-400 mb-1.5 flex items-center"> <ListChecks className="w-3.5 h-3.5 mr-1.5 text-purple-400" strokeWidth={2}/> Improvement Areas</h4>
                                 {analysis.improvementAreas.critical?.length > 0 && <AnalysisListSection title="Critical" items={analysis.improvementAreas.critical} itemColor="text-red-300" />}
                                 {analysis.improvementAreas.high?.length > 0 && <AnalysisListSection title="High Priority" items={analysis.improvementAreas.high} itemColor="text-yellow-300" />}
                                 {analysis.improvementAreas.medium?.length > 0 && <AnalysisListSection title="Medium Priority" items={analysis.improvementAreas.medium} />}
                             </div>
                        )}
                    </div>
                </details>

                 {/* --- Benchmark Section (Optional) --- */}
                 {(benchmark.industry_comparison || benchmark.historical_trend || benchmark.percentile_ranking) && (
                     <details className="mb-4">
                        <summary className="text-sm font-semibold text-gray-300 hover:text-white cursor-pointer select-none mb-3 flex items-center gap-1.5">
                           <Scale className="w-4 h-4 text-purple-400" /> Benchmarks
                        </summary>
                         <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pl-1">
                            {benchmark.percentile_ranking && <MetricCard title="Overall Percentile" value={benchmark.percentile_ranking?.overallPerformance || benchmark.percentile_ranking?.resolution} unit="th" icon={TrendingUp} description="Compared to similar agents" />}
                            {benchmark.industry_comparison && <MetricCard title="Industry Comparison" value={benchmark.industry_comparison?.overallPerformance} icon={Globe} description="Overall performance rating" />}
                            {benchmark.historical_trend && <MetricCard title="Performance Trend" value={benchmark.historical_trend?.performance} icon={ActivityIcon} description={`WoW: ${benchmark.historical_trend?.weekOverWeek || 'N/A'}`} />}
                        </div>
                     </details>
                 )}

            </div> {/* End Body Content */}
        </div> // End Main Result Wrapper
    );
};


// --- Simulation Page Content (API URLs Verified) ---
const SimulationPageContent = () => {
    const { user } = useUser();
    const cid = user?.id;

    // State Declarations...
    const [selectedAgentId, setSelectedAgentId] = useState('');
    const [customerType, setCustomerType] = useState('');
    const [numMessages, setNumMessages] = useState('10'); // Default to 10
    const [agents, setAgents] = useState([]);
    const [simulationData, setSimulationData] = useState({ status: 'idle', results: null, error: null });
    const [isSimulating, setIsSimulating] = useState(false);
    const [formError, setFormError] = useState(null);
    const [agentsError, setAgentsError] = useState(null);
    const [isLoadingAgents, setIsLoadingAgents] = useState(true);

    // --- Fetch Agents useEffect --- (Using original AGENT_FETCH_URL)
    useEffect(() => {
        const fetchAgents = async () => {
            if (!cid) { console.log("Simulations: Waiting for user ID (cid)..."); setIsLoadingAgents(false); return; }
            setIsLoadingAgents(true); setAgentsError(null); setAgents([]);
            try {
                // *** VERIFIED Agent Fetch URL ***
                console.log(`Simulations: Fetching agents for CID: ${cid} from ${AGENT_FETCH_URL}`);
                const response = await axios.post(AGENT_FETCH_URL, { cid });
                const fetchedAgents = response.data.agents || [];
                setAgents(fetchedAgents);
                if (selectedAgentId && !fetchedAgents.some(a => a._id === selectedAgentId)) { setSelectedAgentId(''); }
                if (fetchedAgents.length === 0) { setAgentsError("No agents found for this account."); }
            } catch (err) { console.error("Simulations: Fetch Agents Error:", err); const errorMsg = err.response?.data?.details || err.response?.data?.message || err.message || 'Failed to fetch agents'; setAgentsError(errorMsg); setAgents([]); setSelectedAgentId('');
            } finally { setIsLoadingAgents(false); }
        };
        fetchAgents();
    }, [cid]); // Removed selectedAgentId dependency to prevent refetch loop on select change


    // --- Handle Running Simulation (Using original API_BASE_URL) ---
    const handleRunSimulation = async () => {
        setFormError(null);
        if (!selectedAgentId) { setFormError("Please select an agent."); return; }
        if (!customerType.trim()) { setFormError("Please enter a customer/user type description."); return; }
        if (!numMessages) { setFormError("Please select the number of messages."); return; }

        setIsSimulating(true);
        setSimulationData({ status: 'loading', results: null, error: null }); // Set status to loading immediately

        const requestData = {
            payload: {
                cid: cid,
                agent_id: selectedAgentId,
                user_type: customerType.trim(),
                num_messages: parseInt(numMessages, 10),
            }
        };
        // *** VERIFIED Simulation Request URL ***
        const simulationUrl = `${API_BASE_URL}/echidna/simulate-interaction`;
        console.log(`Running simulation at ${simulationUrl} with request data:`, JSON.stringify(requestData));

        try {
            const response = await axios.post(simulationUrl, requestData, {
                headers: { 'Content-Type': 'application/json' },
                // timeout: 120000 // Optional timeout
            });
            console.log("Simulation API response:", response.data);

            if (response.data && response.data.success && response.data.data) {
                setSimulationData({ status: 'success', results: response.data.data, error: null });
            } else {
                throw new Error(response.data?.details || response.data?.error || "Simulation completed but returned unexpected data format.");
            }
        } catch (err) {
            console.error("Error running simulation:", err);
            let errorMessage = "An unknown simulation error occurred.";
            if (err.response) { errorMessage = `Simulation Failed (${err.response.status}): ${err.response.data?.details || err.response.data?.error || 'Server error.'}`; }
            else if (err.request) { errorMessage = 'No response from simulation server.'; }
            else { errorMessage = `Simulation request error: ${err.message}`; }
            setSimulationData({ status: 'error', results: null, error: errorMessage });
        } finally {
            setIsSimulating(false);
        }
    };

    const messageOptions = [5, 10, 20, 50];
    const canSubmit = selectedAgentId && customerType.trim() && numMessages && !isSimulating && !isLoadingAgents && !agentsError;

    // --- Render Simulation Page Content --- (Unchanged from previous)
    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <header className="mb-6 flex-shrink-0">
                <div className="flex items-center gap-3 mb-1">
                    <FlaskConical className="w-7 h-7 text-purple-500 flex-shrink-0" strokeWidth={1.5} />
                    <h1 className="text-3xl font-bold tracking-tight text-gray-100">Run Simulations</h1>
                </div>
                <p className="text-base text-gray-400 sm:ml-10">Test agent performance by simulating conversations.</p>
            </header>

            {/* Agent Fetching Error Display */}
             {agentsError && !isLoadingAgents && (
                <div className="mb-4 p-3 border border-red-700/50 bg-red-900/20 rounded-lg text-sm text-red-300 flex items-start gap-2 flex-shrink-0" id="agent-error-desc">
                    <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-400" strokeWidth={2}/>
                    <span>{agentsError}</span>
                </div>
             )}

            {/* Configuration Form */}
             <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 flex-shrink-0">
                 {/* Agent Select */}
                 <div>
                     <label htmlFor="agentSelect" className="block text-xs font-medium text-gray-400 mb-1">Select Agent</label>
                     <div className="relative">
                         <select id="agentSelect" value={selectedAgentId} onChange={(e) => { setFormError(null); setSelectedAgentId(e.target.value); }} className="w-full bg-gray-900 border border-gray-700 rounded-md pl-3 pr-10 py-1.5 text-sm text-white focus:outline-none focus:border-purple-600 focus:ring-1 focus:ring-purple-600 appearance-none disabled:opacity-50 disabled:cursor-not-allowed" disabled={isLoadingAgents || isSimulating || agents.length === 0 || !!agentsError} >
                             <option value="" disabled> {isLoadingAgents ? "Loading agents..." : agentsError ? "Error loading agents" : agents.length === 0 && !isLoadingAgents ? "No agents available" : "Select an agent..."} </option>
                             {agents.map(agent => ( <option key={agent._id} value={agent._id}> {agent.agentName || `Agent ID: ${agent._id}`} </option> ))}
                         </select>
                         <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                     </div>
                 </div>
                 {/* Customer Type Input */}
                 <div>
                     <label htmlFor="customerType" className="block text-xs font-medium text-gray-400 mb-1">Customer / User Type</label>
                     <input type="text" id="customerType" value={customerType} onChange={(e) => { setFormError(null); setCustomerType(e.target.value); }} placeholder="e.g., Frustrated user checking status" className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-1.5 text-sm text-white focus:outline-none focus:border-purple-600 focus:ring-1 focus:ring-purple-600 placeholder-gray-500 disabled:opacity-50" disabled={isSimulating} />
                 </div>
                 {/* Number of Messages */}
                 <div>
                     <label htmlFor="numMessages" className="block text-xs font-medium text-gray-400 mb-1">Number of Messages</label>
                      <div className="relative">
                         <select id="numMessages" value={numMessages} onChange={(e) => { setFormError(null); setNumMessages(e.target.value); }} className="w-full bg-gray-900 border border-gray-700 rounded-md pl-3 pr-10 py-1.5 text-sm text-white focus:outline-none focus:border-purple-600 focus:ring-1 focus:ring-purple-600 appearance-none disabled:opacity-50" disabled={isSimulating} >
                             {messageOptions.map(num => ( <option key={num} value={num}>{num} messages</option> ))}
                         </select>
                          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                      </div>
                 </div>
            </div>

            {/* Form Validation Error Display */}
            {formError && (
                <div className="mb-4 p-3 border border-red-700/50 bg-red-900/20 rounded-lg text-sm text-red-300 flex items-start gap-2 flex-shrink-0">
                    <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-400" strokeWidth={2}/>
                    <span>{formError}</span>
                </div>
            )}

            {/* Run Simulation Button */}
            <div className="mb-6 flex-shrink-0">
                <ClickableDiv onClick={handleRunSimulation} disabled={!canSubmit} className={`w-full md:w-auto px-6 py-2 text-sm border flex items-center justify-center gap-2 rounded-md transition-all duration-150 ${ !canSubmit ? 'border-gray-700 bg-gray-800 text-gray-500 cursor-not-allowed' : 'border-purple-700/50 bg-purple-600 hover:bg-purple-700 text-white cursor-pointer focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black' }`} title={ isLoadingAgents ? "Waiting for agents..." : agentsError ? "Cannot run due to agent error." : !selectedAgentId ? "Select agent" : !customerType.trim() ? "Enter customer type" : isSimulating ? "Running..." : "Run Simulation" } >
                    {isSimulating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" strokeWidth={2}/>}
                    <span>{isSimulating ? 'Running...' : 'Run Simulation'}</span>
                </ClickableDiv>
            </div>

            {/* Results Area */}
             <div className="flex-grow min-h-0"> {/* Ensure this takes remaining space */}
                <SimulationResultDisplay simulationData={simulationData} />
            </div>
        </div>
    );
};


// --- Main Application Component (EchSim - Simplified) ---
function EchSim() {
    // Fixed page title
    const pageTitle = 'Simulations';

    // Content is always SimulationPageContent
    const renderContent = () => <SimulationPageContent />;

    return (
        <div className="flex h-screen bg-black font-sans antialiased text-gray-200">
            {/* Simplified Sidebar */}
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* TopBar displays fixed title and updated user info */}
                <TopBar pageTitle={pageTitle} />
                {/* Main content area renders SimulationPageContent */}
                <main className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700/50 scrollbar-track-black relative bg-black p-6 lg:p-8">
                    <div className="relative z-10 h-full">
                        {renderContent()}
                    </div>
                </main>
            </div>
            {/* Global styles (unchanged) */}
            <style jsx global>{`
              /* Echtools global styles */
              .scrollbar-thin { scrollbar-width: thin; scrollbar-color: theme(colors.gray.700 / 0.5) transparent; }
              .scrollbar-thin::-webkit-scrollbar { width: 5px; height: 5px; }
              .scrollbar-thin::-webkit-scrollbar-thumb { background-color: theme(colors.gray.700 / 0.5); border-radius: 3px; }
              .scrollbar-thin::-webkit-scrollbar-track { background-color: transparent; }
              body { background-color: black; /* Removed overflow: hidden; to allow main scroll */ margin: 0; }
              @keyframes bounce-quick { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
              .animate-bounce-quick { animation: bounce-quick 0.3s ease-in-out; }
              @keyframes fade-in-fast { from { opacity: 0; } to { opacity: 1; } }
              .animate-fade-in-fast { animation: fade-in-fast 0.2s ease-out forwards; }
              @keyframes spin { to { transform: rotate(360deg); } }
              .animate-spin { animation: spin 1s linear infinite; }

              /* Prose adjustments */
               .prose { font-size: inherit; line-height: inherit; }
               .prose-invert { color: inherit; }
               .prose code::before, .prose code::after { content: ''; }
               .prose pre { background-color: #1e1e1e !important; padding: 0.8em 1em !important; margin-top: 0.5em !important; margin-bottom: 0.8em !important; border-radius: 0.375rem; overflow-x: auto; white-space: pre; position: relative; z-index: 0; }
               .prose pre code { background-color: transparent !important; padding: 0 !important; font-size: 0.875em !important; }
               .prose-sm { font-size: 0.875rem; line-height: 1.6; }
               .prose-xs { font-size: 0.75rem; line-height: 1.5; }
               .prose div[class*="syntaxhighlighter"] { margin: 0 !important; border-radius: inherit; padding: 0 !important; background: transparent !important; }
               .prose code { white-space: pre-wrap; word-wrap: break-word; }
               .prose pre code { white-space: pre; word-wrap: normal; overflow-x: auto;}

               /* Textarea transition */
               textarea { transition: height 0.1s ease-out; }

               /* Select dropdown arrows */
               select { background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e"); background-position: right 0.5rem center; background-repeat: no-repeat; background-size: 1.5em 1.5em; padding-right: 2.5rem; }
               select:disabled { opacity: 0.5; background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%234b5563' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e"); }
          `}</style>
        </div>
    );
}

export default EchSim;